import { useMemo, useState, type ReactElement } from 'react';
import type { BookingField, BookingFieldOption, Uuid } from '@zenda/api-contract';
import { useT } from '../lib/i18n';
import { useAgendaIntegration } from '../lib/queries';

/**
 * Os campos que a plataforma do cliente exige na hora de marcar.
 *
 * ── Por que isto existe como área genérica ──
 * Hoje é um campo, e é uma exigência da Docplanner: `address_service_id`. Resolver só
 * para ela seria escrever o nome de um produto de terceiro no meio da caixa de
 * marcação, e o próximo fornecedor que exija convênio, código de procedimento ou três
 * coisas pediria a mesma cirurgia outra vez.
 *
 * O adaptador **declara** os campos, o servidor os expõe, e a tela monta o formulário
 * a partir da declaração. Um fornecedor novo não toca este arquivo.
 *
 * ── Por que as opções vêm do vínculo, e não da plataforma ──
 * Quem marca está ao telefone. Uma chamada de rede aqui transformaria instabilidade
 * na plataforma em "não consigo marcar" — e a lista de opções muda de mês em mês, não
 * de minuto em minuto. O retrato é salvo quando alguém configura o mapeamento.
 */
export interface CamposDaIntegracao {
  readonly campos: readonly BookingField[];
  readonly opcoes: Readonly<Record<string, readonly BookingFieldOption[]>>;
  /** O que vai no pedido: os padrões do vínculo, com o que foi escolhido por cima. */
  readonly valores: Readonly<Record<string, string>>;
  readonly escolher: (nome: string, valor: string) => void;
  /** Rótulos dos obrigatórios sem valor. Vazio quando pode marcar. */
  readonly faltando: readonly string[];
}

export function useCamposDaIntegracao(clientId: Uuid, locationId: Uuid | null): CamposDaIntegracao {
  const integracao = useAgendaIntegration(clientId);
  const [escolhas, setEscolhas] = useState<Readonly<Record<string, string>>>({});

  const vinculo = (integracao.data?.links ?? []).find(
    (l) => l.active && l.locationId === locationId,
  );
  const daPlataforma = (integracao.data?.platforms ?? []).find((p) => p.id === vinculo?.platform);

  /**
   * Sem vínculo ativo naquele local, nenhum campo aparece.
   *
   * É o caso do cliente em agenda Zenda — e também do cliente em modo espelho cujo
   * local não foi mapeado, onde a marcação vai ser recusada de qualquer forma. Pedir
   * um campo que não vai a lugar nenhum seria pedir trabalho por nada.
   */
  const campos = vinculo === undefined ? [] : (daPlataforma?.bookingFields ?? []);
  const opcoes = vinculo?.bookingFieldOptions ?? {};

  /**
   * Derivado, e não um `useEffect` que semeia o estado.
   *
   * Com efeito, o valor inicial é copiado uma vez e passa a divergir do vínculo
   * quando o padrão muda no meio da sessão. Assim o padrão é sempre o atual, e a
   * escolha da atendente continua ganhando dele.
   */
  const valores = useMemo(
    () => ({ ...(vinculo?.bookingFieldDefaults ?? {}), ...escolhas }),
    [vinculo?.bookingFieldDefaults, escolhas],
  );

  const faltando = campos
    .filter((c) => c.required && (valores[c.name] ?? '') === '')
    .map((c) => c.label);

  return {
    campos,
    opcoes,
    valores,
    escolher: (nome, valor) => setEscolhas((v) => ({ ...v, [nome]: valor })),
    faltando,
  };
}

/** Renderiza o que `useCamposDaIntegracao` devolveu. Nada, quando não há campo. */
export function CamposDaIntegracaoForm({
  estado,
}: {
  readonly estado: CamposDaIntegracao;
}): ReactElement | null {
  const t = useT();
  if (estado.campos.length === 0) return null;

  return (
    <div className="cint">
      {estado.campos.map((c) => {
        const ops = estado.opcoes[c.name] ?? [];
        const valor = estado.valores[c.name] ?? '';
        return (
          <label key={c.name} className="cint__campo">
            <span className="cint__rotulo">
              {c.label}
              {c.required ? <span className="cint__obrig"> *</span> : null}
            </span>

            {c.type === 'choice' ? (
              /*
                Lista vazia num campo obrigatório é configuração incompleta, e dizer
                isso aqui é melhor que uma caixa vazia: sem opção, a marcação vai ser
                recusada, e quem está na tela precisa saber por quê.
              */
              ops.length === 0 ? (
                <span className="cint__vazio">{t.agenda.configNoService}</span>
              ) : (
                <select
                  className="select select--sm"
                  value={valor}
                  onChange={(e) => estado.escolher(c.name, e.target.value)}
                >
                  {c.required ? null : <option value="">—</option>}
                  {ops.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label ?? o.value}
                    </option>
                  ))}
                </select>
              )
            ) : c.type === 'boolean' ? (
              <input
                type="checkbox"
                checked={valor === 'true'}
                onChange={(e) => estado.escolher(c.name, e.target.checked ? 'true' : '')}
              />
            ) : (
              <input
                className="input input--sm"
                value={valor}
                maxLength={200}
                onChange={(e) => estado.escolher(c.name, e.target.value)}
              />
            )}

            {c.hint === undefined ? null : <span className="cint__dica">{c.hint}</span>}
          </label>
        );
      })}
    </div>
  );
}
